import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PaidIcon from "@mui/icons-material/Paid";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import EventIcon from "@mui/icons-material/Event";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import dayjs from "dayjs";
import MemberPortalGuard from "./MemberPortalGuard";
import { getMemberDashboard } from "../../api/memberPortalClient";
import Popup from "../../base-components/Popup";
import {
  SectionCard,
  StatCard,
  LoadingSkeleton,
  StatusChip,
  formatMoney,
  accountBalanceDisplay,
  formatDate,
  initials,
} from "./components/MemberUI";

function subscriptionProgress(start, end) {
  if (!start || !end) return null;
  const s = dayjs(start);
  const e = dayjs(end);
  const now = dayjs();
  if (!s.isValid() || !e.isValid() || !e.isAfter(s)) return null;
  const total = e.diff(s, "day");
  const elapsed = Math.min(Math.max(now.diff(s, "day"), 0), total);
  const pct = total > 0 ? Math.round((elapsed / total) * 100) : 0;
  const daysLeft = Math.max(e.diff(now, "day"), 0);
  return { pct, daysLeft, total };
}

function MemberDashboardContent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { ok, body } = await getMemberDashboard();
        if (cancelled) return;
        if (!ok) {
          setError(body?.error?.message || "Failed to load dashboard.");
          setPopupOpen(true);
          return;
        }
        setData(body.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load dashboard.");
          setPopupOpen(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const progress = useMemo(
    () =>
      subscriptionProgress(
        data?.current_subscription?.renewed_date ||
          data?.current_subscription?.start_date,
        data?.current_subscription?.end_date
      ),
    [data]
  );

  if (loading) {
    return <LoadingSkeleton rows={4} />;
  }

  const pkg = data?.current_package;
  const hasTrainer = data?.has_trainer === true;
  const member = data?.member;
  const accountBalance = accountBalanceDisplay(data?.account_balance);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="gx-glass rounded-gx p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 border-l-[3px] border-l-gx-red">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-gx bg-gx-red/10 text-gx-red flex items-center justify-center text-2xl md:text-3xl font-bold">
          {initials(member?.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-2xl md:text-3xl font-bold text-gx-ink truncate tracking-tight">
              Welcome{member?.name ? `, ${member.name}` : ""}
            </h1>
            <StatusChip
              active={member?.is_active !== false}
              label={member?.is_active !== false ? "Active" : "Inactive"}
            />
          </div>
          <p className="text-gx-ink-soft text-sm">
            Member code{" "}
            <span className="font-semibold text-gx-ink">
              {member?.member_code || "—"}
            </span>
            {member?.location?.name ? ` · ${member.location.name}` : ""}
            {member?.company?.company_name || member?.company?.name
              ? ` · ${member.company.company_name || member.company.name}`
              : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/member/invoices")}
          className="gx-cta shrink-0"
        >
          View invoices
        </button>
      </div>

      {/* Financial strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          featured
          label="Pending Amount"
          value={formatMoney(data?.pending_amount)}
          icon={<PendingActionsIcon />}
          accent="#F7931E"
          onClick={() => navigate("/member/invoices")}
          hint={`${data?.pending_invoices_count ?? 0} open invoice(s)`}
        />
        <StatCard
          label={accountBalance.label}
          value={accountBalance.value}
          icon={<AccountBalanceWalletIcon />}
          accent={accountBalance.accent}
          hint={accountBalance.hint}
        />
        <StatCard
          label="Pending Invoices"
          value={data?.pending_invoices_count ?? 0}
          icon={<ReceiptLongIcon />}
          accent="#D71920"
          onClick={() => navigate("/member/invoices")}
        />
        <StatCard
          label="Paid Invoices"
          value={data?.paid_invoices_count ?? 0}
          icon={<PaidIcon />}
          accent="#059669"
          onClick={() => navigate("/member/invoices")}
        />
      </div>

      {/* Package spotlight */}
      <SectionCard
        title="Current Package"
        subtitle={
          pkg
            ? `${pkg.duration || "Membership"}${
                pkg.isFinite ? " · Fixed term" : " · Recurring"
              }`
            : "No package on file"
        }
        icon={<CardMembershipIcon />}
        action={
          <button
            type="button"
            onClick={() => navigate("/member/profile")}
            className="text-sm font-medium text-gx-red hover:underline"
          >
            Profile
          </button>
        }
      >
        {pkg ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div>
                <p className="text-2xl font-bold text-gx-ink">{pkg.title}</p>
                {pkg.description ? (
                  <p className="text-sm text-gx-ink-soft mt-1">{pkg.description}</p>
                ) : null}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-gx-canvas border border-gx-border p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gx-ink-soft font-semibold">
                    Membership fee
                  </p>
                  <p className="text-lg font-bold text-gx-ink mt-1">
                    {formatMoney(pkg.membership_fee)}
                  </p>
                </div>
                <div className="rounded-xl bg-gx-canvas border border-gx-border p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gx-ink-soft font-semibold">
                    Facilities fee
                  </p>
                  <p className="text-lg font-bold text-gx-ink mt-1">
                    {formatMoney(pkg.facilities_fee)}
                  </p>
                </div>
                <div className="rounded-xl bg-gx-canvas border border-gx-border p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gx-ink-soft font-semibold">
                    Registration
                  </p>
                  <p className="text-lg font-bold text-gx-ink mt-1">
                    {formatMoney(pkg.registration_fee)}
                  </p>
                </div>
              </div>

              {pkg.facilities?.length ? (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft mb-2 flex items-center gap-1">
                    <LocalOfferIcon sx={{ fontSize: 14 }} /> Included facilities
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {pkg.facilities.map((f) => (
                      <span
                        key={String(f._id)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gx-navy/5 text-gx-navy ring-1 ring-gx-navy/10"
                      >
                        {f.name}
                        {f.fee > 0 ? (
                          <span className="text-gx-ink-soft">
                            · {formatMoney(f.fee)}
                          </span>
                        ) : (
                          <span className="text-emerald-600">· Free</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl bg-gx-canvas border border-gx-border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-gx-ink">
                <EventIcon fontSize="small" className="text-gx-red" />
                Subscription window
              </div>
              <div className="text-sm space-y-2">
                <div className="flex justify-between gap-2">
                  <span className="text-gx-ink-soft">Renewed</span>
                  <span className="font-medium text-gx-ink">
                    {formatDate(data?.current_subscription?.renewed_date)}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gx-ink-soft">
                    {pkg.isFinite ? "Expires" : "Next renewal"}
                  </span>
                  <span className="font-medium text-gx-ink">
                    {formatDate(
                      pkg.isFinite
                        ? data?.package_expires_on
                        : data?.subscription_renewal_date
                    )}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gx-ink-soft">Next invoice</span>
                  <span className="font-medium text-gx-ink">
                    {data?.next_invoice_date
                      ? formatDate(data.next_invoice_date)
                      : pkg.isFinite
                        ? `Expires ${formatDate(data?.package_expires_on)}`
                        : "—"}
                  </span>
                </div>
              </div>

              {progress ? (
                <div className="pt-2">
                  <div className="flex justify-between text-xs text-gx-ink-soft mb-1">
                    <span>Cycle progress</span>
                    <span>{progress.daysLeft} day(s) left</span>
                  </div>
                  <div className="h-2 rounded-full bg-gx-border overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-gx-navy to-gx-red transition-all"
                      style={{ width: `${progress.pct}%` }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gx-border bg-gx-canvas px-4 py-8 text-center">
            <p className="text-sm font-medium text-gx-ink">
              Package details are not available for your account yet.
            </p>
            <p className="text-xs text-gx-ink-soft mt-1">
              Please contact your club if you believe this is incorrect.
            </p>
          </div>
        )}
      </SectionCard>

      {/* Trainer — only when assigned */}
      {hasTrainer ? (
        <SectionCard
          title="Your Trainer"
          subtitle="Personal training assignment"
          icon={<FitnessCenterIcon />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1 rounded-xl bg-gx-canvas border border-gx-border p-4">
              <p className="text-xs uppercase tracking-wide text-gx-ink-soft font-semibold">
                Assigned trainer
              </p>
              <p className="text-xl font-bold text-gx-ink mt-1">
                {data?.assigned_trainer?.name || "Assigned"}
              </p>
              {data?.assigned_trainer?.phone ? (
                <p className="text-sm text-gx-ink-soft mt-1">
                  {data.assigned_trainer.phone}
                </p>
              ) : null}
            </div>
            <StatCard
              label="Trainer period start"
              value={formatDate(data?.trainer_start)}
              icon={<EventIcon />}
              accent="#033b51"
            />
            <StatCard
              label="Next trainer invoice"
              value={formatDate(data?.next_trainer_invoice_date)}
              icon={<EventIcon />}
              accent="#F7931E"
            />
          </div>
        </SectionCard>
      ) : null}

      {popupOpen && (
        <Popup
          alertType="error"
          message={error}
          onClose={() => setPopupOpen(false)}
        />
      )}
    </div>
  );
}

function MemberDashboard() {
  return (
    <MemberPortalGuard>
      <MemberDashboardContent />
    </MemberPortalGuard>
  );
}

export default MemberDashboard;
